//components/estimation-factor-details/EstimationFactorDetails.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Table, Tag, Typography, Spin, Alert, Row, Col, Tooltip, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import styles from './EstimationFactorDetails.module.css';
import useEstimationDetails from './useEstimationDetails';
import type { EstimationRowBase } from '@custom-types/estimation';
import { NSPButton } from '@components/ui/NSPButton';
import NSPCheckbox from '@components/ui/NSPCheckbox';
import { toTestAttrValue, withTestColumnAttrs, type TestAttrs } from '@utils/testAttr';
import type { ButtonProps } from 'antd';

const { Title, Text } = Typography;

type SummaryRow = {
  key: 'summary';
  name: string;
  meterId: '';
  lastMonth: number;
  thisMonth: number;
  percentChange: string;
  include: null;
  readType: '';
};

type DividerRow = {
  key: 'divider';
  isDivider: true;
};

type TableRow = EstimationRowBase | SummaryRow | DividerRow;

interface EstimationFactorCardProps {
  estimationFactor: number;
  onRecalculate: () => void;
  isRecalculating: boolean;
  hasUnsavedChanges: boolean;
  isMonthLocked: boolean;
  readOnly: boolean;
}

const EstimationFactorCard = ({
  estimationFactor,
  onRecalculate,
  isRecalculating,
  hasUnsavedChanges,
  isMonthLocked,
  readOnly,
}: EstimationFactorCardProps) => {
  const isButtonDisabled = readOnly || isMonthLocked || !hasUnsavedChanges || isRecalculating;
  let tooltipText: string;
  if (readOnly) {
    tooltipText = 'You have read-only access and cannot recalculate the estimation factor';
  } else if (isMonthLocked) {
    tooltipText = 'Estimation factors are read only — a meter for this month has been approved';
  } else if (hasUnsavedChanges) {
    tooltipText = '';
  } else {
    tooltipText = 'Please check or uncheck a meter to enable recalculation';
  }

  return (
    <div className={styles.estimationCard} data-testid="estimation-factor-card">
      <Title level={3} style={{ fontSize: '18px', marginBottom: '8px' }}>
        Estimation Factor Calculation
      </Title>
      <Row className={styles.estimationdivInner}>
        <Col span={14} className={styles.summaryBlock}>
          <Title level={4} className={styles.summaryLabel}>
            Estimation Factor
          </Title>
          <Text className={styles.summaryValue} style={{ color: '#2563eb' }} data-testid="estimation-factor-value">
            {Number.isNaN(estimationFactor)
              ? '--'
              : `${Number(estimationFactor).toFixed(2)}%`}
          </Text>
        </Col>
        <Col span={14} className={styles.summaryBlock}>
          <Title level={4} className={styles.summaryLabel}>
            Action
          </Title>
          <Tooltip title={tooltipText}>
            <NSPButton
              onClick={onRecalculate}
              loading={isRecalculating}
              disabled={isButtonDisabled}
              data-testid="recalculate-button"
              data-tooltip={tooltipText || undefined}
            >
              {isRecalculating ? 'Recalculating...' : 'Recalculate'}
            </NSPButton>
          </Tooltip>
        </Col>
      </Row>
    </div>
  );
};

const getTagClassName = (readType: string): string => {
  if (readType === 'Actual') return 'tag-actual';
  if (readType === 'Scada') return 'tag-scada';
  return 'tag-estimate';
};

const getRowClassName = (record: TableRow): string => {
  if (record.key === 'summary') return styles.collapsedRow;
  if ('isDivider' in record) return styles.dividerRow;
  const r = record as EstimationRowBase;
  if (r.approvalStatus === 'Approved') return styles.rowApproved;
  return r.include ? styles.rowIncluded : styles.rowExcluded;
};

// Playwright attributes: meter rows carry identity/state; the totals and divider rows have their own IDs
const getRowTestAttrs = (record: TableRow): TestAttrs => {
  if (record.key === 'summary') return { 'data-testid': 'summary-row' } as TestAttrs;
  if ('isDivider' in record) return { 'data-testid': 'excluded-divider' } as TestAttrs;
  const r = record as EstimationRowBase;
  return {
    'data-testid': 'meter-row',
    'data-meter-id': String(r.meterId),
    'data-meter-number': r.meterNumber ?? '',
    'data-included': String(Boolean(r.include)),
    'data-approved': String(r.approvalStatus === 'Approved'),
    'data-read-type': toTestAttrValue(r.readType),
    'data-prior-read-type': toTestAttrValue(r.priorMonthReadType),
  } as TestAttrs;
};

const getValidationAlertTitle =(status: 'error' | 'warning' | 'success'): React.ReactElement => {
  if (status === 'error') return <strong>Error</strong>;
  if (status === 'warning') return <strong>Warning</strong>;
  return <strong>Success</strong>;
};

export interface EstimationFactorDetailsProps {
  energyType: string;
  region: string;
  month: string;
  readOnly?: boolean;
}

const EstimationFactorDetails = ({ energyType, region, month, readOnly = false }: EstimationFactorDetailsProps) => {
  const navigate = useNavigate();

  const {
    localData,
    toggleInclude,
    recalculate,
    loading,
    error,
    isRecalculating,
    hasUnsavedChanges,
    isMonthLocked,
    total,
    validationFeedback,
  } = useEstimationDetails({
    month_year: month,
    region,
    energy_type: energyType,
  });

  const [showValidationAlert, setShowValidationAlert] = useState(true);

  useEffect(() => {
    setShowValidationAlert(true);
  }, [region, energyType, month]);

  // Warn on browser back/forward/close when include changes are unsaved
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    const handlePopState = () => {
      if (hasUnsavedChanges) {
        globalThis.history.pushState(null, '', globalThis.location.href);
        Modal.confirm({
          title: 'Unsaved changes',
          content:
            'You have changed which meters to include but not recalculated the factor. Do you want to continue?',
          okText: 'Continue',
          cancelText: 'Stay',
          okButtonProps: { 'data-testid': 'modal-ok-button' } as ButtonProps,
          cancelButtonProps: { 'data-testid': 'modal-cancel-button' } as ButtonProps,
          onOk: () => navigate(-1),
        });
      }
    };

    globalThis.history.pushState(null, '', globalThis.location.href);
    globalThis.addEventListener('beforeunload', handleBeforeUnload);
    globalThis.addEventListener('popstate', handlePopState);

    return () => {
      globalThis.removeEventListener('beforeunload', handleBeforeUnload);
      globalThis.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, navigate]);

  const groupLabel = energyType && region
    ? `${energyType.charAt(0).toUpperCase()}${energyType.slice(1)} ${region.charAt(0).toUpperCase()}${region.slice(1)}`
    : '';

  const formatNumber = (val: number | string) => Number(val || 0).toLocaleString();

  const hasActiveData = !!(energyType && region && month);

  const tableData: EstimationRowBase[] = hasActiveData
    ? [...localData.used, ...localData.excluded]
        .filter((meter) => meter.readType !== 'N/A' && meter.thisMonth != null)
        .map((meter, index) => ({ key: index, ...meter }))
    : [];

  const includedRows = tableData.filter((m) => m.include && m.thisMonth != null);
  const excludedRows = tableData.filter((m) => !m.include || m.thisMonth == null);

  const summaryData: SummaryRow = {
    key: 'summary',
    name: 'Net units total',
    meterId: '',
    lastMonth: total(includedRows, 'lastMonth'),
    thisMonth: total(includedRows, 'thisMonth'),
    percentChange:
      total(includedRows, 'lastMonth') !== 0
        ? `${((total(includedRows, 'thisMonth') / total(includedRows, 'lastMonth')) * 100).toFixed(2)}%`
        : '0%',
    include: null,
    readType: '',
  };

  const dividerRow: DividerRow = { key: 'divider', isDivider: true };

  const allTableData: TableRow[] = hasActiveData
    ? [
        ...includedRows,
        summaryData,
        ...(excludedRows.length > 0 ? [dividerRow, ...excludedRows] : []),
      ]
    : [];

  const columns: ColumnsType<TableRow> = [
    {
      title: 'Generation Type/Region',
      dataIndex: 'name',
      key: 'name',
      className: styles.thLeft,
      width: 200,
      render: (text, record) => {
        if ('isDivider' in record) return null;
        return (
          <Text className={styles.tdNowrap}>
            {record.key === 'summary' ? <Text strong>{text as string}</Text> : text}
          </Text>
        );
      },
      onCell: (record) => {
        if ('isDivider' in record) return { colSpan: 8, className: styles.dividerCell };
        return {};
      },
    },
    {
      title: 'Meter Number',
      dataIndex: 'meterNumber',
      key: 'meterNumber',
      className: styles.thLeft,
      width: 130,
      render: (text, record) => {
        if ('isDivider' in record) return null;
        if (record.key === 'summary') return <Text strong>{text as string}</Text>;
        const r = record as EstimationRowBase;
        return (
          <Link to={`/meterreaddetails/${r.meterId}`} data-testid="meter-number-link">
            {text as string}
          </Link>
        );
      },
      onCell: (record) => {
        if ('isDivider' in record) return { colSpan: 0 };
        return {};
      },
    },
    {
      title: 'Prior Net Gen kWh',
      dataIndex: 'lastMonth',
      key: 'lastMonth',
      className: styles.thRight,
      width: 180,
      render: (value, record) => {
        if ('isDivider' in record) return null;
        const roundedValue = Math.round(Number(value) || 0);
        return record.key === 'summary' ? (
          <Text strong>{formatNumber(roundedValue)}</Text>
        ) : (
          <Text className={styles.tdNowrap}>{formatNumber(roundedValue)}</Text>
        );
      },
      onCell: (record) => {
        if ('isDivider' in record) return { colSpan: 0 };
        return {};
      },
    },
    {
      title: 'Prior Month Read Type',
      dataIndex: 'priorMonthReadType',
      key: 'priorMonthReadType',
      className: styles.thLeft,
      width: 160,
      render: (priorMonthReadType, record) => {
        if ('isDivider' in record || record.key === 'summary') return null;
        const tagClassName = `tag ${getTagClassName(priorMonthReadType as string)}`;
        return <Tag className={tagClassName}>{priorMonthReadType as string}</Tag>;
      },
      onCell: (record) => {
        if ('isDivider' in record) return { colSpan: 0 };
        return {};
      },
    },
    {
      title: 'Current Net Gen kWh',
      dataIndex: 'thisMonth',
      key: 'thisMonth',
      className: styles.thRight,
      width: 180,
      render: (value, record) => {
        if ('isDivider' in record) return null;
        const roundedValue = Math.round(Number(value) || 0);
        return record.key === 'summary' ? (
          <Text strong>{formatNumber(roundedValue)}</Text>
        ) : (
          <Text className={styles.tdNowrap}>{formatNumber(roundedValue)}</Text>
        );
      },
      onCell: (record) => {
        if ('isDivider' in record) return { colSpan: 0 };
        return {};
      },
    },
    {
      title: 'Current Month Read Type',
      dataIndex: 'readType',
      key: 'readType',
      className: styles.thLeft,
      width: 140,
      render: (readType, record) => {
        if ('isDivider' in record || record.key === 'summary') return null;
        const tagClassName = `tag ${getTagClassName(readType as string)}`;
        return <Tag className={tagClassName}>{readType as string}</Tag>;
      },
      onCell: (record) => {
        if ('isDivider' in record) return { colSpan: 0 };
        return {};
      },
    },
    {
      title: '% Change Over Last Month',
      dataIndex: 'percentChange',
      key: 'percentChange',
      className: styles.thRight,
      width: 180,
      render: (value, record) => {
        if ('isDivider' in record) return null;
        if (record.key === 'summary') return <Text strong>{value as string}</Text>;
        const v = value as number | null;
        if (v == null) return <Text className={styles.tdNowrap}>N/A</Text>;
        return (
          <Text className={styles.tdNowrap} style={{ fontWeight: 'bold' }}>
            {v.toFixed(2)}%
          </Text>
        );
      },
      onCell: (record) => {
        if ('isDivider' in record) return { colSpan: 0 };
        return {};
      },
    },
    {
      title: 'Include In Factor',
      dataIndex: 'include',
      key: 'include',
      className: styles.thCenter,
      width: 120,
      render: (_checked, record) => {
        if ('isDivider' in record || record.key === 'summary') return null;
        const estimationRecord = record as EstimationRowBase;
        const isEstimate = ['estimate', 'zero'].includes(estimationRecord.readType?.toLowerCase() || '');
        const isApproved = estimationRecord.approvalStatus === 'Approved';
        const checkboxDisabled = readOnly || isEstimate || isApproved || isMonthLocked || isRecalculating;
        let tooltipMsg: string;
        if (readOnly) {
          tooltipMsg = 'You have read-only access and cannot modify this meter';
        } else if (isMonthLocked) {
          tooltipMsg = 'Estimation factors are read only — a meter for this month has been approved';
        } else if (isApproved) {
          tooltipMsg = 'This meter is approved and cannot be modified';
        } else {
          tooltipMsg = '';
        }
        return (
          <Tooltip title={tooltipMsg}>
            <NSPCheckbox
              checked={estimationRecord.include}
              onChange={() => toggleInclude(estimationRecord.meterId)}
              disabled={checkboxDisabled}
              style={checkboxDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              data-testid="include-checkbox"
              data-tooltip={tooltipMsg || undefined}
            />
          </Tooltip>
        );
      },
      onCell: (record) => {
        if ('isDivider' in record) return { colSpan: 0 };
        return {};
      },
    },
  ];

  return (
    <Row className={styles.container}>
      <Col span={24} className={styles.innerContainer}>

        {hasActiveData && groupLabel && (
          <div
            className={styles.groupLabelRow}
            data-testid="group-label"
            data-energy-type={toTestAttrValue(energyType)}
            data-region={toTestAttrValue(region)}
            data-month={month}
          >
            <Title level={4} style={{ margin: 0 }}>{groupLabel}</Title>
            <Text type="secondary" data-testid="group-period">Period: {month}</Text>
          </div>
        )}

        {hasActiveData && (
          <>
            {loading ? (
              <Row className={styles.centered} style={{ minHeight: 200 }}>
                <Col><Spin size="large" data-testid="loading-spinner" /></Col>
              </Row>
            ) : error ? (
              <Row className={styles.centered}>
                <Col>
                  <Alert
                    message={typeof error === 'string' ? error : (error?.message ?? 'Unknown error')}
                    type="error"
                    showIcon
                    data-testid="estimation-error-alert"
                  />
                </Col>
              </Row>
            ) : (
              <>
                <Row style={{ marginBottom: '20px' }}>
                  <Col span={24}>
                    <EstimationFactorCard
                      estimationFactor={localData.estimationFactor}
                      onRecalculate={recalculate}
                      isRecalculating={isRecalculating}
                      hasUnsavedChanges={hasUnsavedChanges}
                      isMonthLocked={isMonthLocked}
                      readOnly={readOnly}
                    />
                  </Col>
                </Row>

                {validationFeedback && showValidationAlert && (
                  <Row className={styles.validationAlertRow}>
                    <Col span={24} className={styles.validationAlertCol}>
                      <Alert
                        message={getValidationAlertTitle(validationFeedback.status)}
                        description={
                          <div className={styles.validationAlertList}>{validationFeedback.message}</div>
                        }
                        type={validationFeedback.status}
                        showIcon
                        closable
                        onClose={() => setShowValidationAlert(false)}
                        data-testid="validation-alert"
                        data-type={validationFeedback.status}
                      />
                    </Col>
                  </Row>
                )}

                <div className={styles.tableContainer}>
                  <Title level={3} style={{ fontSize: '18px', marginBottom: '10px' }}>
                    Readings Used to Calculate Estimation Factor
                  </Title>
                  <Table<TableRow>
                    columns={withTestColumnAttrs(columns, 'meter-value')}
                    dataSource={allTableData}
                    pagination={false}
                    bordered={false}
                    size="small"
                    className={styles.table}
                    scroll={{ x: 'max-content' }}
                    rowClassName={getRowClassName}
                    data-testid="estimation-readings-table"
                    data-row-count={String(tableData.length)}
                    onRow={getRowTestAttrs}
                  />
                </div>
              </>
            )}
          </>
        )}

        {!hasActiveData && (
          <Row className={styles.emptyState} data-testid="estimation-empty-state">
            <Col>
              <Text type="secondary">Select a region and generation type, then click Display Data.</Text>
            </Col>
          </Row>
        )}

      </Col>
    </Row>
  );
};

export default EstimationFactorDetails;
